import React, { useEffect, useRef, useState } from 'react';
import { ArrowRight } from 'lucide-react';
import HeroVisual from './HeroVisual';

const HeroSection = () => {
  const sectionRef = useRef(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const node = sectionRef.current;
    if (!node) return undefined;

    // Trigger entrance shortly after mount so users always see the cinematic reveal
    // on the homepage (the hero is in-view on load). IntersectionObserver also
    // re-fires when the section scrolls back into view from below.
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true);
        }
      },
      { threshold: 0.15 }
    );
    observer.observe(node);

    // Fallback: ensure entrance plays even if observer is delayed
    const t = setTimeout(() => setInView(true), 250);

    return () => {
      observer.disconnect();
      clearTimeout(t);
    };
  }, []);

  const scrollToContact = () => {
    const element = document.getElementById('contact');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const scrollToSolutions = () => {
    const element = document.getElementById('solutions');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section
      ref={sectionRef}
      className={`hero-section ${inView ? 'is-in-view' : ''}`}
      style={{
        background: 'var(--bg-primary)',
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        padding: '0 7.6923%',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      <div
        style={{
          maxWidth: '1400px',
          margin: '0 auto',
          width: '100%',
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: '60px',
          alignItems: 'center',
        }}
        className="hero-grid"
      >
        {/* Left Content */}
        <div style={{ zIndex: 10 }}>
          <div
            className="hero-reveal hero-reveal--1"
            style={{
              display: 'inline-block',
              padding: '8px 16px',
              background: 'rgba(184, 134, 11, 0.08)',
              border: '1px solid var(--brand-primary)',
              marginBottom: '30px',
              fontSize: '14px',
              color: 'var(--brand-primary)',
              fontWeight: '500',
            }}
          >
            BUSINESS CONSULTANCY & TECHNOLOGY
          </div>

          <h1 className="display-huge hero-reveal hero-reveal--2" style={{ marginBottom: '30px' }}>
            Innovating Solutions.
            <br />
            <span style={{ color: 'var(--brand-primary)' }}>Empowering Business Evolution.</span>
          </h1>

          <p
            className="body-large hero-reveal hero-reveal--3"
            style={{ marginBottom: '40px', maxWidth: '600px' }}
          >
            VDM Business Innovations harnesses next-generation technology to create adaptive solutions for the evolving needs of Hospitality, Commercial Real Estate, and beyond.
          </p>

          <div
            className="hero-reveal hero-reveal--4"
            style={{ display: 'flex', gap: '20px', flexWrap: 'wrap' }}
          >
            <button
              data-testid="hero-cta-explore"
              onClick={scrollToSolutions}
              className="btn-primary"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '12px',
              }}
            >
              Explore Our Innovations
              <ArrowRight size={20} />
            </button>
            <button
              data-testid="hero-cta-contact"
              onClick={scrollToContact}
              className="btn-secondary"
            >
              Let's Innovate Together
            </button>
          </div>

          {/* Stats */}
          <div
            className="hero-stats hero-reveal hero-reveal--5"
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(3, 1fr)',
              gap: '30px',
              marginTop: '60px',
            }}
          >
            <div>
              <div style={{ fontSize: '32px', fontWeight: '600', color: 'var(--brand-primary)', marginBottom: '8px' }}>7+</div>
              <div style={{ fontSize: '14px', color: 'var(--text-muted)' }}>Active Innovations</div>
            </div>
            <div>
              <div style={{ fontSize: '32px', fontWeight: '600', color: 'var(--brand-primary)', marginBottom: '8px' }}>100%</div>
              <div style={{ fontSize: '14px', color: 'var(--text-muted)' }}>Client Satisfaction</div>
            </div>
            <div>
              <div style={{ fontSize: '32px', fontWeight: '600', color: 'var(--brand-primary)', marginBottom: '8px' }}>24/7</div>
              <div style={{ fontSize: '14px', color: 'var(--text-muted)' }}>Support</div>
            </div>
          </div>
        </div>

        {/* Right - Themed Orbital Visual */}
        <div
          style={{
            width: '700px',
            height: '700px',
            position: 'relative',
            overflow: 'visible',
          }}
          className="hero-spline"
        >
          <HeroVisual animate={inView} />
        </div>
      </div>

      <style>{`
        /* Initial hidden state for staggered hero reveals */
        .hero-section .hero-reveal {
          opacity: 0;
          transform: translateY(24px);
          transition: opacity 900ms cubic-bezier(0.22, 1, 0.36, 1),
                      transform 900ms cubic-bezier(0.22, 1, 0.36, 1);
          will-change: opacity, transform;
        }
        .hero-section.is-in-view .hero-reveal {
          opacity: 1;
          transform: translateY(0);
        }
        .hero-section.is-in-view .hero-reveal--1 { transition-delay: 80ms; }
        .hero-section.is-in-view .hero-reveal--2 { transition-delay: 220ms; }
        .hero-section.is-in-view .hero-reveal--3 { transition-delay: 380ms; }
        .hero-section.is-in-view .hero-reveal--4 { transition-delay: 540ms; }
        .hero-section.is-in-view .hero-reveal--5 { transition-delay: 700ms; }

        @media (prefers-reduced-motion: reduce) {
          .hero-section .hero-reveal {
            opacity: 1 !important;
            transform: none !important;
            transition: none !important;
          }
        }

        @media (max-width: 1024px) {
          .hero-grid {
            grid-template-columns: 1fr !important;
            gap: 40px !important;
          }
          .hero-spline {
            width: 100% !important;
            height: 500px !important;
            max-width: 600px;
            margin: 0 auto;
          }
          .hero-stats {
            grid-template-columns: repeat(3, 1fr) !important;
          }
        }
        @media (max-width: 768px) {
          .display-huge {
            font-size: 40px !important;
          }
          .hero-spline {
            height: 400px !important;
          }
        }
      `}</style>
    </section>
  );
};

export default HeroSection;
